import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { array, fn } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import { service } from "@ember/service";
import DMenu from "discourse/float-kit/components/d-menu";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import DButton from "discourse/ui-kit/d-button";
import DDropdownMenu from "discourse/ui-kit/d-dropdown-menu";
import dDiscourseTag from "discourse/ui-kit/helpers/d-discourse-tag";
import { i18n } from "discourse-i18n";
import {
  MIN_CHARACTER_COUNT,
  showSuggestionsError,
} from "../../lib/ai-helper-suggestions";

export default class AiTagSuggester extends Component {
  @service siteSettings;
  @service toasts;
  @service composer;

  @tracked loading = false;
  @tracked suggestions = null;
  @tracked triggerIcon = "discourse-sparkles";
  dMenu;

  get content() {
    return this.args.composer?.reply;
  }

  get model() {
    return this.args.composer || this.args.buffered;
  }

  get showSuggestionButton() {
    if (this.composer.disableTagsChooser) {
      return false;
    }

    const showTrigger =
      this.content?.length > MIN_CHARACTER_COUNT ||
      this.args.topicState === "edit";

    document
      .querySelector(".composer-fields")
      ?.classList.toggle("showing-ai-suggestions", showTrigger);

    return this.siteSettings.ai_embeddings_enabled && showTrigger;
  }

  get showDropdown() {
    return !this.loading && this.suggestions?.length > 0;
  }

  get tagSelectorHasValues() {
    return this.model.get("tags")?.length > 0;
  }

  @action
  async loadSuggestions() {
    if (this.suggestions?.length > 0 && !this.dMenu.expanded) {
      return this.suggestions;
    }

    this.loading = true;
    this.triggerIcon = "spinner";

    const data = {};

    if (this.content) {
      data.text = this.content;
    } else {
      data.topic_id = this.args.buffered.content.id;
    }

    try {
      const { assistant } = await ajax("/discourse-ai/ai-helper/suggest_tags", {
        method: "POST",
        data,
      });

      this.suggestions = assistant;

      if (this.tagSelectorHasValues) {
        this.suggestions = this.suggestions.filter(
          (s) => !this.model.get("tags").some((t) => t.id === s.id)
        );
      }

      if (this.suggestions?.length === 0) {
        showSuggestionsError(this, this.loadSuggestions.bind(this));
        return;
      }
    } catch (error) {
      popupAjaxError(error);
    } finally {
      this.loading = false;
      this.triggerIcon = "rotate";
    }

    return this.suggestions;
  }

  #removeAppliedTag(suggestion) {
    this.suggestions = this.suggestions.filter((s) => s.id !== suggestion.id);
  }

  @action
  applySuggestion(suggestion) {
    const maxTags = this.siteSettings.max_tags_per_topic;
    if (!this.model) {
      return;
    }

    const tags = this.model.get("tags");

    if (!tags) {
      this.model.set("tags", [suggestion]);
      this.#removeAppliedTag(suggestion);
      return;
    }

    if (tags.length >= maxTags) {
      return this.toasts.error({
        class: "ai-suggestion-error",
        duration: "short",
        data: {
          message: i18n("discourse_ai.ai_helper.suggest_errors.too_many_tags", {
            count: maxTags,
          }),
        },
      });
    }

    tags.push(suggestion);
    this.model.set("tags", [...tags]);
    suggestion.disabled = true;
    this.#removeAppliedTag(suggestion);
  }

  @action
  onRegisterApi(api) {
    this.dMenu = api;
  }

  @action
  onClose() {
    if (this.suggestions?.length > 0) {
      // If all suggestions have been used,
      // re-triggering when no suggestions present
      // will cause computation issues with
      // setting the icon, so we prevent it
      this.triggerIcon = "discourse-sparkles";
    }
  }

  <template>
    {{#if this.showSuggestionButton}}
      <DMenu
        @title={{i18n "discourse_ai.ai_helper.suggest"}}
        @icon={{this.triggerIcon}}
        @identifier="ai-tag-suggester"
        @onClose={{this.onClose}}
        @triggerClass="btn-transparent suggestion-button suggest-tags-button {{if
          this.loading
          'is-loading'
        }}"
        @contentClass="ai-suggestions-menu"
        @onRegisterApi={{this.onRegisterApi}}
        @modalForMobile={{true}}
        @untriggers={{array}}
        {{on "click" this.loadSuggestions}}
      >
        <:content>
          {{#if this.showDropdown}}
            <DDropdownMenu as |dropdown|>
              {{#each this.suggestions as |suggestion index|}}
                <dropdown.item>
                  <DButton
                    class="tag-row"
                    data-name={{suggestion.name}}
                    data-value={{index}}
                    title={{suggestion.name}}
                    @translatedLabel={{suggestion.name}}
                    @disabled={{suggestion.disabled}}
                    @action={{fn this.applySuggestion suggestion}}
                  >
                    {{dDiscourseTag
                      suggestion.name
                      count=suggestion.count
                      noHref=true
                    }}
                  </DButton>
                </dropdown.item>
              {{/each}}
            </DDropdownMenu>
          {{/if}}
        </:content>
      </DMenu>
    {{/if}}
  </template>
}
